// PANOUL INSTALAȚIILOR, în meniul vizualizatorului: elementul ales, verificările (un clic arată elementele vinovate), valorile
// folosite cu sursa lor — ce e implicit sau sugerat e „de confirmat” (plan §2)
// — și cantitățile pe sistem și diametru, cum intră în deviz.

import { useFormat, useT, type Valori } from "../i18n";
import { useInstalatii } from "./context";
import { SISTEME, cantitati, lungime, type ModelInstalatii, type Sistem } from "./model";
import "./instalatii.css";

export default function PanouInstalatii() {
  const t = useT();
  const { numar } = useFormat();
  const inst = useInstalatii();
  if (!inst) return null;
  const numeSistem = (s: Sistem) => t(SISTEME.find((x) => x.sistem === s)!.nume);
  // cifrele din parametri, cu virgula sau punctul limbii, nu cu punctul din model
  const valori = (p?: Record<string, string | number>): Valori | undefined => p && Object.fromEntries(
    Object.entries(p).map(([k, v]) => [k, typeof v === "number" ? numar(v, { maximumFractionDigits: 2 }) : v]));

  const m = inst.model;
  return (
    <div className="inst-panou">
      {inst.ales && <Ales m={m} id={inst.ales} numeSistem={numeSistem} />}
      <div>
        <b>{t("inst.verificari")}</b>
        {m.verificari.map((v) => (
          <button key={v.regula} type="button" className="inst-verif" disabled={!v.elemente.length}
                  title={v.sursa}
                  onClick={() => inst.alege(v.elemente[0] ?? null)}>
            <span className={`inst-verdict ${v.verdict}`}>{v.verdict}</span>
            <span>
              {t(v.mesaj, valori(v.param))}
              <br /><span className="inst-sursa">{v.regula} · {v.sursa}</span>
            </span>
          </button>
        ))}
      </div>
      {m.intrari.length > 0 && <div>
        <b>{t("inst.intrari")}</b>
        <table className="inst-tabel"><tbody>
          {m.intrari.map((x) => (
            <tr key={x.cheie}>
              <td>{t(x.cheie)}</td>
              <td>{t(x.valoare, valori(x.param))}
                <span className="inst-sursa"> · {t(`inst.sursa.${x.sursa}` as const)}</span></td>
            </tr>
          ))}
        </tbody></table>
      </div>}
      <div>
        <b>{t("inst.cantitati")}</b>
        <table className="inst-tabel"><tbody>
          {cantitati(m).map((q) => (
            <tr key={`${q.sistem}-${q.dn}`}>
              <td>{numeSistem(q.sistem)}</td>
              <td className="nr">{!q.dn ? "—" : q.sistem === "electrice" ? `${q.dn} mm²` : `DN ${q.dn}`}</td>
              <td className="nr">{numar(q.ml, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

/** Ce e elementul ales: sistemul, rolul, diametrul, panta, materialul, pe unde
 *  trece și cât e de lung — ce scrie un proiectant lângă o țeavă pe planșă. */
function Ales({ m, id, numeSistem }: { m: ModelInstalatii; id: string; numeSistem: (s: Sistem) => string }) {
  const t = useT();
  const { numar } = useFormat();
  const cm = (x: number) => numar(x * 100, { maximumFractionDigits: 0 });
  const doua = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const s = m.segmente.find((x) => x.id === id);
  const e = s ? null : m.echipamente.find((x) => x.id === id);
  if (!s && !e) return null;
  const randuri: [string, string][] = s ? [
    [t("inst.camp.sistem"), numeSistem(s.sistem)],
    [t("inst.camp.rol"), t(`inst.rol.${s.rol}` as const)],
    [t("inst.camp.diametru"), s.sistem === "electrice" ? (s.dn ? `${s.dn} mm²` : "—")
      : s.sectiune ? `${cm(s.sectiune.l)} × ${cm(s.sectiune.h)} cm`
      : s.dn ? `DN ${s.dn}` : "—"],
    ...(s.panta ? [[t("inst.camp.panta"), `${numar(s.panta * 100, { maximumFractionDigits: 1 })} %`] as [string, string]] : []),
    [t("inst.camp.material"), t(`inst.material.${s.material}` as const)],
    [t("inst.camp.mediu"), t(`inst.mediu.${s.mediu}` as const)],
    [t("inst.camp.lungime"), `${numar(lungime(s.puncte), doua)} m`],
  ] : [
    [t("inst.camp.sistem"), numeSistem(e!.sistem)],
    [t("inst.camp.echipament"), t(`inst.echipament.${e!.fel}` as const)],
    [t("inst.camp.dimensiuni"), e!.dim.map((x) => numar(x, doua)).join(" × ") + " m"],
  ];
  return (
    <div className="card">
      <table className="inst-tabel"><tbody>
        {randuri.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}
      </tbody></table>
    </div>
  );
}
